import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import { TabNamesEnum } from '../../Company/CompanyTabs/CompanyTabs';
import {
  CompaniesProps,
  CompaniesProviderProps,
  CompanyContextActions,
  CompanyContextActionTypes,
} from './CompanyContext.types';

const CompaniesContext = createContext<CompaniesProps | undefined>(undefined);
const CompaniesDispatchContext = createContext<Dispatch<CompanyContextActions> | undefined>(
  undefined,
);
export const START_PAGINATION_PAGE = 1;

const initialCompaniesState: CompaniesProps = {
  companies: [],
  searchId: undefined,
  dataset: undefined,
  dbVersion: undefined,
  company: undefined,
  pagination: { page: START_PAGINATION_PAGE, size: 50, totalPages: 0, totalElements: 0 },
  selectedCompanies: [],
  selectedFiles: [],
  filterButtonClickState: false,
  isLoading: false,
  companyGeneralInfo: {},
  clickedCompanyTabName: TabNamesEnum.COMPANY,
};

const CompaniesReducer = (state: CompaniesProps, action: CompanyContextActions) => {
  switch (action.type) {
    case CompanyContextActionTypes.SetInitial: {
      return { ...state };
    }
    case CompanyContextActionTypes.SetCompanies: {
      return {
        ...state,
        searchId: action.payload.searchId,
        dataset: action.payload.dataset,
        dbVersion: action.payload.dbVersion,
        companies: [...action.payload.companies],
      };
    }
    case CompanyContextActionTypes.SetCompany: {
      return {
        ...state,
        company: action.payload ? { ...state.company, ...action.payload } : undefined,
      };
    }
    case CompanyContextActionTypes.SetPagination: {
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload },
      };
    }
    case CompanyContextActionTypes.ResetPagination: {
      return {
        ...state,
        pagination: initialCompaniesState.pagination,
      };
    }
    case CompanyContextActionTypes.SelectCompanies: {
      return {
        ...state,
        selectedCompanies: action.payload,
      };
    }
    case CompanyContextActionTypes.SelectFiles: {
      return {
        ...state,
        selectedFiles: action.payload,
      };
    }
    case CompanyContextActionTypes.SetFilterButtonClickState: {
      return {
        ...state,
        filterButtonClickState: action.payload,
      };
    }
    case CompanyContextActionTypes.SetIsLoadingState: {
      return {
        ...state,
        isLoading: action.payload,
      };
    }
    case CompanyContextActionTypes.SetCompanyGeneralInfo: {
      return {
        ...state,
        companyGeneralInfo: action.payload,
      };
    }
    case CompanyContextActionTypes.SetClickedCompanyTabName: {
      return {
        ...state,
        clickedCompanyTabName: action.payload,
      };
    }
    case CompanyContextActionTypes.SetDatasetInfo: {
      return {
        ...state,
        dataset: action.payload.dataset ?? state.dataset,
        dbVersion: action.payload.dbVersion ?? state.dbVersion,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

export const CompaniesContextProvider = ({ children }: CompaniesProviderProps) => {
  const [state, dispatch] = useReducer(CompaniesReducer, initialCompaniesState);

  return (
    <CompaniesContext.Provider value={state}>
      <CompaniesDispatchContext.Provider value={dispatch}>
        {children}
      </CompaniesDispatchContext.Provider>
    </CompaniesContext.Provider>
  );
};

export const useCompaniesState = () => {
  const context = useContext(CompaniesContext);

  if (context === undefined) {
    throw new Error('CompaniesState must be used within a CompaniesProvider');
  }

  return context;
};

export const useCompaniesDispatch = () => {
  const context = useContext(CompaniesDispatchContext);

  if (context === undefined) {
    throw new Error('CompaniesDispatch must be used within a CompaniesProvider');
  }

  return context;
};
